import React, { useEffect, useState, useCallback, useMemo } from "react";
import * as XLSX from "xlsx";

import {
  getHolidaysApi,
  addHolidaysApi,
  updateHolidaysApi,
  deleteHolidaysApi,
} from "./holidayApi";
import { getSecureItem } from "../../utilsFunctions";

// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  bg: "#F8FAFC",
  white: "#fff",
  border: "#E2E8F0",
  borderLight: "#F1F5F9",
  text: "#0F172A",
  textMid: "#1E293B",
  textMuted: "#64748B",
  textFaint: "#94A3B8",
  accent: "#00D95E",
  accentDark: "#01a046",
  accentBg: "#F0FDFA",
  accentText: "#01a046",
  danger: "#DC2626",
  dangerBg: "#FFF5F5",
  dangerBorder: "#FEE2E2",
  dangerHover: "#FEE2E2",
  warningBg: "#FFFBEB",
  warningBorder: "#FDE68A",
  warningText: "#92400E",
  successBg: "#F0FDF4",
  successBorder: "#BBF7D0",
  successText: "#166534",
  purple: "#7C3AED",
  purpleBg: "#F5F3FF",
  purpleBorder: "#DDD6FE",
  shadow: "0 1px 4px rgba(15,23,42,0.06)",
  shadowModal: "0 20px 60px rgba(15,23,42,0.18)",
  radius: "14px",
  radiusSm: "10px",
  radiusXs: "8px",
  font: "'Lato', 'Segoe UI', system-ui, sans-serif",
};

// ─── Global styles ────────────────────────────────────────────────────────────
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Lato:wght@400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; }
  input[type="text"]:focus, input[type="date"]:focus {
    border-color: ${T.accent} !important;
    background: ${T.white} !important;
    box-shadow: 0 0 0 3px rgba(13,148,136,0.12);
  }
  .hol-tr:hover td { background: ${T.bg}; }
  .hol-tr:hover .row-accent { opacity: 1 !important; }
  .hol-btn-outline:hover { border-color: #CBD5E1 !important; color: ${T.text} !important; background: ${T.bg} !important; }
  .hol-btn-primary:hover { background: ${T.accentDark} !important; }
  .hol-btn-primary:active { transform: scale(0.97); }
  .hol-btn-cancel:hover { background: ${T.bg} !important; }
  .hol-icon-btn:hover { border-color: ${T.accent} !important; color: ${T.accent} !important; background: ${T.accentBg} !important; }
  .hol-icon-danger:hover { background: ${T.dangerHover} !important; border-color: ${T.danger} !important; }
  .hol-alert-enter { animation: alertSlideIn 0.25s ease; }
  @keyframes alertSlideIn {
    from { opacity: 0; transform: translateY(-8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .hol-modal-enter { animation: modalPop 0.22s ease; }
  @keyframes modalPop {
    from { opacity: 0; transform: scale(0.96) translateY(8px); }
    to   { opacity: 1; transform: scale(1) translateY(0); }
  }
  .hol-table-wrap {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  .hol-sunday-row:hover { background: ${T.bg} !important; }
  .hol-sunday-row:hover .sun-check { border-color: ${T.accent} !important; }
  .hol-year-chip { transition: all 0.15s ease; cursor: pointer; user-select: none; }
  .hol-year-chip:hover { opacity: 0.88; }
  .hol-sunday-list::-webkit-scrollbar { width: 4px; }
  .hol-sunday-list::-webkit-scrollbar-track { background: transparent; }
  .hol-sunday-list::-webkit-scrollbar-thumb { background: ${T.border}; border-radius: 4px; }
`;

// ─── SVG Icons ────────────────────────────────────────────────────────────────
const Icon = {
  Search: () => (
    <svg
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  ),
  Upload: () => (
    <svg
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  ),
  Download: () => (
    <svg
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  ),
  Plus: () => (
    <svg
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      viewBox="0 0 24 24"
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  Edit: () => (
    <svg
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  ),
  Trash: () => (
    <svg
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  ),
  Calendar: () => (
    <svg
      width="14"
      height="14"
      fill="none"
      stroke={T.textFaint}
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  Check: () => (
    <svg
      width="11"
      height="11"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      viewBox="0 0 24 24"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  Warn: () => (
    <svg
      width="20"
      height="20"
      fill="none"
      stroke="#D97706"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  Success: () => (
    <svg
      width="20"
      height="20"
      fill="none"
      stroke="#16A34A"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  ),
  Info: () => (
    <svg
      width="20"
      height="20"
      fill="none"
      stroke={T.accent}
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  ),
  X: () => (
    <svg
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      viewBox="0 0 24 24"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
};

// ─── Utilities ────────────────────────────────────────────────────────────────
function formatDate(raw) {
  if (!raw) return "—";
  const d = new Date(raw);
  if (isNaN(d)) return raw;
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

// Pad to YYYY-MM-DD
function toISODate(d) {
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const dy = String(d.getDate()).padStart(2, "0");
  return `${yr}-${mo}-${dy}`;
}

// Return all Sundays in a given year as { holidayName, holidayDate }
function getSundaysOfYear(year) {
  const sundays = [];
  const d = new Date(year, 0, 1);
  // advance to first Sunday
  d.setDate(d.getDate() + ((7 - d.getDay()) % 7));
  while (d.getFullYear() === year) {
    sundays.push({
      holidayName: "Sunday",
      holidayDate: toISODate(new Date(d)),
    });
    d.setDate(d.getDate() + 7);
  }
  return sundays;
}

const AVAILABLE_YEARS = [2025, 2026, 2027, 2028, 2029, 2030];
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// ─── Alert component ──────────────────────────────────────────────────────────
let alertCounter = 0;

function Alert({ alerts, onDismiss }) {
  if (!alerts.length) return null;
  const cfg = {
    success: {
      bg: T.successBg,
      border: T.successBorder,
      color: T.successText,
      Icon: Icon.Success,
    },
    warning: {
      bg: T.warningBg,
      border: T.warningBorder,
      color: T.warningText,
      Icon: Icon.Warn,
    },
    info: {
      bg: T.accentBg,
      border: "#99F6E4",
      color: T.accentText,
      Icon: Icon.Info,
    },
    danger: {
      bg: T.dangerBg,
      border: T.dangerBorder,
      color: T.danger,
      Icon: Icon.Warn,
    },
  };
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        marginBottom: "16px",
      }}
    >
      {alerts.map((a) => {
        const c = cfg[a.type] || cfg.info;
        return (
          <div
            key={a.id}
            className="hol-alert-enter"
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "10px",
              padding: "12px 14px",
              background: c.bg,
              border: `1.5px solid ${c.border}`,
              borderRadius: T.radiusSm,
              color: c.color,
            }}
          >
            <span style={{ flexShrink: 0, marginTop: "1px" }}>
              <c.Icon />
            </span>
            <span
              style={{
                flex: 1,
                fontSize: "13.5px",
                fontFamily: T.font,
                fontWeight: 500,
                lineHeight: 1.5,
              }}
            >
              {a.msg}
            </span>
            <button
              onClick={() => onDismiss(a.id)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: c.color,
                padding: "2px",
                flexShrink: 0,
              }}
            >
              <Icon.X />
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ─── Confirm Dialog ───────────────────────────────────────────────────────────
function ConfirmDialog({ config, onConfirm, onCancel }) {
  if (!config) return null;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1100,
        padding: "16px",
        backdropFilter: "blur(3px)",
      }}
    >
      <div
        className="hol-modal-enter"
        style={{
          background: T.white,
          borderRadius: T.radius,
          width: "100%",
          maxWidth: "380px",
          boxShadow: T.shadowModal,
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "24px 24px 0" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "12px",
            }}
          >
            <span
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                background: T.dangerBg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Icon.Warn />
            </span>
            <h3
              style={{
                margin: 0,
                fontSize: "16px",
                fontWeight: 700,
                color: T.text,
                fontFamily: T.font,
              }}
            >
              {config.title}
            </h3>
          </div>
          <p
            style={{
              margin: "0 0 20px",
              fontSize: "14px",
              color: T.textMuted,
              lineHeight: 1.6,
              fontFamily: T.font,
            }}
          >
            {config.message}
          </p>
        </div>
        <div
          style={{
            padding: "16px 24px",
            borderTop: `1px solid ${T.borderLight}`,
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
          }}
        >
          <button
            className="hol-btn-cancel"
            onClick={onCancel}
            style={{
              padding: "9px 18px",
              border: `1.5px solid ${T.border}`,
              borderRadius: T.radiusSm,
              background: T.white,
              color: T.textMuted,
              fontSize: "13.5px",
              fontFamily: T.font,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: "9px 18px",
              border: "none",
              borderRadius: T.radiusSm,
              background: T.danger,
              color: T.white,
              fontSize: "13.5px",
              fontWeight: 600,
              cursor: "pointer",
              boxShadow: "0 1px 3px rgba(220,38,38,0.3)",
              fontFamily: T.font,
            }}
          >
            {config.confirmLabel || "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Sunday Picker Panel ──────────────────────────────────────────────────────
function SundayPicker({ selectedKeys, onToggle, onToggleAll }) {
  const [activeYears, setActiveYears] = useState([new Date().getFullYear()]);

  // All sundays for active years, grouped by year then month
  const grouped = useMemo(() => {
    const result = {};
    activeYears.forEach((yr) => {
      result[yr] = {};
      getSundaysOfYear(yr).forEach((s) => {
        const mo = parseInt(s.holidayDate.split("-")[1]) - 1;
        if (!result[yr][mo]) result[yr][mo] = [];
        result[yr][mo].push(s);
      });
    });
    return result;
  }, [activeYears]);

  const allKeys = useMemo(() => {
    const keys = [];
    activeYears.forEach((yr) => {
      getSundaysOfYear(yr).forEach((s) => keys.push(s.holidayDate));
    });
    return keys;
  }, [activeYears]);

  const allChecked =
    allKeys.length > 0 && allKeys.every((k) => selectedKeys.has(k));
  const someChecked = allKeys.some((k) => selectedKeys.has(k));

  const toggleYear = (yr) => {
    setActiveYears((prev) =>
      prev.includes(yr) ? prev.filter((y) => y !== yr) : [...prev, yr].sort(),
    );
  };

  const selectedCount = allKeys.filter((k) => selectedKeys.has(k)).length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {/* Year chips */}
      <div>
        <p
          style={{
            margin: "0 0 8px",
            fontSize: "11.5px",
            fontFamily: T.font,
            fontWeight: 600,
            color: T.textMuted,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          Select Year(s)
        </p>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {AVAILABLE_YEARS.map((yr) => {
            const active = activeYears.includes(yr);
            return (
              <button
                key={yr}
                className="hol-year-chip"
                onClick={() => toggleYear(yr)}
                style={{
                  padding: "5px 14px",
                  borderRadius: "20px",
                  border: `1.5px solid ${active ? T.accent : T.border}`,
                  background: active ? T.accent : T.white,
                  color: active ? T.white : T.textMuted,
                  fontSize: "13px",
                  fontFamily: T.font,
                  fontWeight: 600,
                }}
              >
                {yr}
              </button>
            );
          })}
        </div>
      </div>

      {/* Select all / count row */}
      {activeYears.length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 12px",
            background: T.purpleBg,
            borderRadius: T.radiusXs,
            border: `1px solid ${T.purpleBorder}`,
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
            }}
          >
            <span
              onClick={() => onToggleAll(allKeys, !allChecked)}
              style={{
                width: "17px",
                height: "17px",
                borderRadius: "5px",
                flexShrink: 0,
                border: `2px solid ${allChecked || someChecked ? T.purple : T.border}`,
                background: allChecked
                  ? T.purple
                  : someChecked
                    ? T.purpleBg
                    : T.white,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              {allChecked && (
                <span style={{ color: T.white }}>
                  <Icon.Check />
                </span>
              )}
              {!allChecked && someChecked && (
                <span
                  style={{
                    width: "8px",
                    height: "2px",
                    background: T.purple,
                    borderRadius: "1px",
                    display: "block",
                  }}
                />
              )}
            </span>
            <span
              style={{
                fontSize: "13px",
                fontFamily: T.font,
                fontWeight: 600,
                color: T.purple,
              }}
            >
              Select all Sundays
            </span>
          </label>
          <span
            style={{
              fontSize: "12px",
              fontFamily: T.font,
              fontWeight: 600,
              color: T.purple,
              background: T.white,
              padding: "2px 8px",
              borderRadius: "10px",
              border: `1px solid ${T.purpleBorder}`,
            }}
          >
            {selectedCount} / {allKeys.length} selected
          </span>
        </div>
      )}

      {/* Sunday list grouped by year → month */}
      {activeYears.length === 0 ? (
        <p
          style={{
            textAlign: "center",
            color: T.textFaint,
            fontSize: "13px",
            fontFamily: T.font,
            padding: "16px 0",
          }}
        >
          Select at least one year above to see Sundays.
        </p>
      ) : (
        <div
          className="hol-sunday-list"
          style={{
            maxHeight: "260px",
            overflowY: "auto",
            border: `1px solid ${T.border}`,
            borderRadius: T.radiusSm,
          }}
        >
          {activeYears.map((yr) => (
            <div key={yr}>
              {/* Year header */}
              <div
                style={{
                  padding: "7px 14px",
                  background: T.bg,
                  borderBottom: `1px solid ${T.border}`,
                  fontSize: "11px",
                  fontFamily: T.font,
                  fontWeight: 700,
                  color: T.textFaint,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                }}
              >
                {yr}
              </div>

              {Object.entries(grouped[yr] || {}).map(([moIdx, sundays]) => (
                <div key={moIdx}>
                  {/* Month sub-header */}
                  <div
                    style={{
                      padding: "5px 14px",
                      background: "#FAFAFA",
                      borderBottom: `1px solid ${T.borderLight}`,
                      fontSize: "11px",
                      fontFamily: T.font,
                      fontWeight: 600,
                      color: T.textMuted,
                      letterSpacing: "0.04em",
                    }}
                  >
                    {MONTHS[parseInt(moIdx)]}
                  </div>

                  {sundays.map((s) => {
                    const checked = selectedKeys.has(s.holidayDate);
                    const display = formatDate(s.holidayDate);
                    return (
                      <div
                        key={s.holidayDate}
                        className="hol-sunday-row"
                        onClick={() => onToggle(s)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          padding: "8px 14px",
                          cursor: "pointer",
                          borderBottom: `1px solid ${T.borderLight}`,
                          background: checked ? "#FEFEFF" : T.white,
                          transition: "background 0.12s",
                        }}
                      >
                        {/* Custom checkbox */}
                        <span
                          className="sun-check"
                          style={{
                            width: "16px",
                            height: "16px",
                            borderRadius: "4px",
                            flexShrink: 0,
                            border: `2px solid ${checked ? T.accent : T.border}`,
                            background: checked ? T.accent : T.white,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.15s",
                          }}
                        >
                          {checked && (
                            <span style={{ color: T.white }}>
                              <Icon.Check />
                            </span>
                          )}
                        </span>
                        <span
                          style={{
                            fontSize: "13.5px",
                            fontFamily: T.font,
                            color: checked ? T.text : T.textMid,
                            fontWeight: checked ? 600 : 400,
                          }}
                        >
                          {display}
                        </span>
                        {/* Day label (always Sunday) */}
                        <span
                          style={{
                            marginLeft: "auto",
                            fontSize: "11px",
                            fontFamily: T.font,
                            fontWeight: 500,
                            color: T.accent,
                            background: T.accentBg,
                            padding: "2px 8px",
                            borderRadius: "10px",
                          }}
                        >
                          Sun
                        </span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Holiday Modal ────────────────────────────────────────────────────────────
function HolidayModal({
  open,
  isEdit,
  formData,
  onChange,
  onSave,
  onClose,
  onSaveSundays,
}) {
  const [sundayMode, setSundayMode] = useState(false);
  // Set of selected holidayDate strings
  const [selectedKeys, setSelectedKeys] = useState(new Set());

  // Reset sunday state when modal opens/closes
  useEffect(() => {
    if (!open) {
      setSundayMode(false);
      setSelectedKeys(new Set());
    }
  }, [open]);

  const handleToggle = useCallback((sunday) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(sunday.holidayDate)) next.delete(sunday.holidayDate);
      else next.add(sunday.holidayDate);
      return next;
    });
  }, []);

  const handleToggleAll = useCallback((keys, select) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => (select ? next.add(k) : next.delete(k)));
      return next;
    });
  }, []);

  if (!open) return null;

  const handleSave = () => {
    if (sundayMode) {
      if (selectedKeys.size === 0) return;
      // Build payload same shape as bulk Excel add
      const payload = Array.from(selectedKeys)
        .sort()
        .map((date) => ({
          holidayName: "Sunday",
          holidayDate: date,
        }));
      onSaveSundays(payload);
    } else {
      onSave();
    }
  };

  const saveDisabled = sundayMode ? selectedKeys.size === 0 : false;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "16px",
        backdropFilter: "blur(2px)",
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="hol-modal-enter"
        style={{
          background: T.white,
          borderRadius: T.radius,
          width: "100%",
          maxWidth: sundayMode ? "520px" : "440px",
          boxShadow: T.shadowModal,
          overflow: "hidden",
          transition: "max-width 0.2s ease",
        }}
      >
        {/* ── Header ── */}
        <div
          style={{
            padding: "18px 20px 14px",
            borderBottom: `1px solid ${T.borderLight}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "10px",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: "16px",
              fontFamily: T.font,
              fontWeight: 700,
              color: T.text,
              flexShrink: 0,
            }}
          >
            {isEdit ? "Edit Holiday" : "Add Holiday"}
          </h2>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginLeft: "auto",
            }}
          >
            {/* Sunday mode toggle */}
            {!isEdit && (
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "7px",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                {/* Toggle switch */}
                <span
                  onClick={() => setSundayMode((v) => !v)}
                  style={{
                    width: "34px",
                    height: "18px",
                    borderRadius: "9px",
                    background: sundayMode ? T.accent : T.border,
                    position: "relative",
                    display: "inline-block",
                    transition: "background 0.2s",
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                >
                  <span
                    style={{
                      position: "absolute",
                      top: "2px",
                      left: sundayMode ? "18px" : "2px",
                      width: "14px",
                      height: "14px",
                      borderRadius: "50%",
                      background: T.white,
                      transition: "left 0.2s",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                    }}
                  />
                </span>
                <span
                  style={{
                    fontSize: "12.5px",
                    fontFamily: T.font,
                    fontWeight: 600,
                    color: sundayMode ? T.accent : T.textMuted,
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    whiteSpace: "nowrap",
                  }}
                >
                  Add sundays as holidays
                </span>
              </label>
            )}

            {/* Close */}
            <button
              onClick={onClose}
              style={{
                width: "28px",
                height: "28px",
                border: "none",
                background: T.borderLight,
                borderRadius: T.radiusXs,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: T.textMuted,
                flexShrink: 0,
              }}
            >
              <Icon.X />
            </button>
          </div>
        </div>

        {/* ── Body ── */}
        <div
          style={{
            padding: "18px 20px",
            display: "flex",
            flexDirection: "column",
            gap: "14px",
          }}
        >
          {sundayMode ? (
            <SundayPicker
              selectedKeys={selectedKeys}
              onToggle={handleToggle}
              onToggleAll={handleToggleAll}
            />
          ) : (
            <>
              <div>
                <label
                  style={{
                    fontSize: "11.5px",
                    fontFamily: T.font,
                    fontWeight: 600,
                    color: T.textMuted,
                    display: "block",
                    marginBottom: "6px",
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                  }}
                >
                  Holiday Name
                </label>
                <input
                  type="text"
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    border: `1.5px solid ${T.border}`,
                    borderRadius: T.radiusSm,
                    fontSize: "14px",
                    fontFamily: T.font,
                    color: T.text,
                    background: T.bg,
                    outline: "none",
                  }}
                  placeholder="e.g. Diwali"
                  value={formData.holidayName}
                  onChange={(e) =>
                    onChange({ ...formData, holidayName: e.target.value })
                  }
                />
              </div>
              <div>
                <label
                  style={{
                    fontSize: "11.5px",
                    fontFamily: T.font,
                    fontWeight: 600,
                    color: T.textMuted,
                    display: "block",
                    marginBottom: "6px",
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                  }}
                >
                  Date
                </label>
                <input
                  type="date"
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    border: `1.5px solid ${T.border}`,
                    borderRadius: T.radiusSm,
                    fontSize: "14px",
                    fontFamily: T.font,
                    color: T.text,
                    background: T.bg,
                    outline: "none",
                  }}
                  value={formData.holidayDate}
                  onChange={(e) =>
                    onChange({ ...formData, holidayDate: e.target.value })
                  }
                />
              </div>
            </>
          )}
        </div>

        {/* ── Footer ── */}
        <div
          style={{
            padding: "14px 20px",
            borderTop: `1px solid ${T.borderLight}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "10px",
          }}
        >
          {/* Selected count hint in sunday mode */}
          {sundayMode && (
            <span
              style={{
                fontSize: "12.5px",
                fontFamily: T.font,
                color: T.textMuted,
              }}
            >
              {selectedKeys.size > 0 ? (
                <>
                  <b style={{ color: T.accent }}>{selectedKeys.size}</b> Sunday
                  {selectedKeys.size !== 1 ? "s" : ""} selected
                </>
              ) : (
                "No Sundays selected yet"
              )}
            </span>
          )}
          {!sundayMode && <span />}

          <div style={{ display: "flex", gap: "10px" }}>
            <button
              className="hol-btn-cancel"
              onClick={onClose}
              style={{
                padding: "9px 18px",
                border: `1.5px solid ${T.border}`,
                borderRadius: T.radiusSm,
                background: T.white,
                color: T.textMuted,
                fontSize: "13.5px",
                fontFamily: T.font,
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              className="hol-btn-primary"
              onClick={handleSave}
              disabled={saveDisabled}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "9px 18px",
                border: "none",
                borderRadius: T.radiusSm,
                background: saveDisabled ? T.textFaint : T.accent,
                color: T.white,
                fontSize: "13.5px",
                fontFamily: T.font,
                fontWeight: 600,
                cursor: saveDisabled ? "not-allowed" : "pointer",
                boxShadow: saveDisabled
                  ? "none"
                  : "0 1px 3px rgba(13,148,136,0.25)",
                transition: "background 0.2s",
              }}
            >
              {sundayMode
                ? `Add ${selectedKeys.size > 0 ? selectedKeys.size : ""} Sunday${selectedKeys.size !== 1 ? "s" : ""}`
                : isEdit
                  ? "Save Changes"
                  : "Add Holiday"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function Holidays() {
  const companyId = getSecureItem("loggedinUserCompany");

  const [count, setCount] = useState(0);
  const [holidays, setHolidays] = useState([]);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    holidayName: "",
    holidayDate: "",
  });
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [confirmConfig, setConfirmConfig] = useState(null);

  // ── Alert helpers ──────────────────────────────────────────────────────────
  const pushAlert = useCallback((msg, type = "info", duration = 4000) => {
    const id = ++alertCounter;
    setAlerts((prev) => [...prev, { id, msg, type }]);
    if (duration)
      setTimeout(
        () => setAlerts((prev) => prev.filter((a) => a.id !== id)),
        duration,
      );
  }, []);

  const dismissAlert = useCallback((id) => {
    setAlerts((prev) => prev.filter((a) => a.id !== id));
  }, []);

  // ── Confirm helper ─────────────────────────────────────────────────────────
  const askConfirm = (title, message, confirmLabel, onConfirm) => {
    setConfirmConfig({ title, message, confirmLabel, onConfirm });
  };
  const closeConfirm = () => setConfirmConfig(null);

  // ── API calls ──────────────────────────────────────────────────────────────
  const fetchHolidays = useCallback(async () => {
    try {
      const res = await getHolidaysApi(companyId);
      setHolidays(res.data);
      setCount(res.count);
    } catch (e) {
      console.error(e);
      pushAlert("Failed to load holidays. Please refresh.", "danger");
    }
  }, [companyId, pushAlert]);

  useEffect(() => {
    fetchHolidays();
  }, [fetchHolidays]);

  const handleAddHoliday = async () => {
    if (!formData.holidayName.trim() || !formData.holidayDate) {
      pushAlert("Please fill in both Holiday Name and Date.", "warning", 3500);
      return;
    }
    try {
      const response = await addHolidaysApi({ ...formData, companyId });
      setModalOpen(false);
      setFormData({ holidayName: "", holidayDate: "" });
      await fetchHolidays();

      if (response.data?.status) {
        pushAlert(response.data?.msg, "success");
      } else {
        pushAlert(response.data?.msg, "warning");
      }
    } catch (e) {
      console.error(e);
      pushAlert("Failed to add holiday. Please try again.", "danger");
    }
  };

  const handleUpdateHoliday = async () => {
    if (!formData.holidayName.trim() || !formData.holidayDate) {
      pushAlert("Please fill in both Holiday Name and Date.", "warning", 3500);
      return;
    }
    try {
      const response = await updateHolidaysApi(selectedHoliday.holidayId, {
        ...formData,
      });
      setModalOpen(false);
      await fetchHolidays();
      if (response.data?.status) {
        pushAlert(response.data?.msg, "success");
      } else {
        pushAlert(response.data?.msg, "warning");
      }
    } catch (e) {
      console.error(e);
      pushAlert("Failed to update holiday. Please try again.", "danger");
    }
  };

  // Bulk add sundays — same payload shape as Excel upload
  const handleSaveSundays = async (payload) => {
    try {
      const withCompany = payload.map((p) => ({ ...p, companyId }));
      const response = await addHolidaysApi(withCompany);
      setModalOpen(false);
      await fetchHolidays();
      if (response.data?.status) {
        pushAlert(
          `${payload.length} Sunday${payload.length !== 1 ? "s" : ""} added as holidays successfully.`,
          "success",
        );
      } else {
        pushAlert(response.data?.msg, "warning");
      }
    } catch (e) {
      console.error(e);
      pushAlert("Failed to add Sundays. Please try again.", "danger");
    }
  };

  const openEdit = (row) => {
    setSelectedHoliday(row);
    setFormData({ holidayName: row.holidayName, holidayDate: row.holidayDate });
    setModalOpen(true);
  };

  const openAdd = () => {
    setSelectedHoliday(null);
    setFormData({ holidayName: "", holidayDate: "" });
    setModalOpen(true);
  };

  const requestDelete = (row) => {
    askConfirm(
      "Delete Holiday",
      `Are you sure you want to delete "${row.holidayName}"? This action cannot be undone.`,
      "Delete",
      async () => {
        closeConfirm();
        try {
          await deleteHolidaysApi(row.holidayId);
          await fetchHolidays();
          pushAlert(`"${row.holidayName}" has been deleted.`, "warning", 4000);
        } catch (e) {
          console.error(e);
          pushAlert("Failed to delete holiday. Please try again.", "danger");
        }
      },
    );
  };

  const handleExcelUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: "binary" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(sheet);
        if (!jsonData.length) {
          pushAlert("The uploaded file has no data rows.", "warning");
          return;
        }
        const payload = jsonData.map((item) => ({
          holidayName: item.holidayName,
          holidayDate: item.holidayDate,
          companyId,
        }));
        const response = await addHolidaysApi(payload);
        await fetchHolidays();
        if (response.data?.status) {
          pushAlert(
            `${jsonData.length} holiday${jsonData.length > 1 ? "s" : ""} imported successfully.`,
            "success",
          );
        } else {
          pushAlert(response.data?.msg, "warning");
        }
      } catch (err) {
        console.error(err);
        pushAlert(
          "Failed to import file. Check the format and try again.",
          "danger",
        );
      }
    };
    reader.readAsBinaryString(file);
    event.target.value = "";
  };

  const downloadTemplate = () => {
    const data = [
      { holidayName: "New Year", holidayDate: "2027-01-01" },
      { holidayName: "Republic Day", holidayDate: "2027-01-26" },
    ];
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Holidays");
    XLSX.writeFile(workbook, "Holiday_Template.xlsx");
    pushAlert("Template downloaded.", "info", 2500);
  };

  const filteredData = holidays?.filter((item) =>
    item.holidayName?.toLowerCase().includes(search.toLowerCase()),
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{GLOBAL_CSS}</style>

      <div
        style={{
          minHeight: "100vh",
          background: T.bg,
          padding: "28px 20px",
          fontFamily: T.font,
          color: T.text,
        }}
      >
        {/* Alerts */}
        <Alert alerts={alerts} onDismiss={dismissAlert} />

        {/* Toolbar */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "10px",
            marginBottom: "18px",
            alignItems: "center",
          }}
        >
          <div
            style={{
              flex: "1 1 200px",
              minWidth: "140px",
              position: "relative",
            }}
          >
            <span
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: T.textFaint,
                pointerEvents: "none",
                display: "flex",
              }}
            >
              <Icon.Search />
            </span>
            <input
              type="text"
              style={{
                width: "100%",
                padding: "10px 12px 10px 38px",
                border: `1.5px solid ${T.border}`,
                borderRadius: T.radiusSm,
                fontSize: "14px",
                fontFamily: T.font,
                background: T.white,
                color: T.text,
                outline: "none",
              }}
              placeholder="Search holidays…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <label
              className="hol-btn-outline"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "9px 15px",
                border: `1.5px solid ${T.border}`,
                borderRadius: T.radiusSm,
                background: T.white,
                color: T.textMuted,
                fontSize: "13.5px",
                fontFamily: T.font,
                fontWeight: 500,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              <Icon.Upload /> Upload Excel
              <input
                hidden
                type="file"
                accept=".xlsx,.xls"
                onChange={handleExcelUpload}
              />
            </label>

            <button
              className="hol-btn-outline"
              onClick={downloadTemplate}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "9px 15px",
                border: `1.5px solid ${T.border}`,
                borderRadius: T.radiusSm,
                background: T.white,
                color: T.textMuted,
                fontSize: "13.5px",
                fontFamily: T.font,
                fontWeight: 500,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              <Icon.Download /> Template
            </button>

            <button
              className="hol-btn-primary"
              onClick={openAdd}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "9px 16px",
                border: "none",
                borderRadius: T.radiusSm,
                background: T.accent,
                color: T.white,
                fontSize: "13.5px",
                fontFamily: T.font,
                fontWeight: 600,
                cursor: "pointer",
                whiteSpace: "nowrap",
                boxShadow: "0 1px 3px rgba(13,148,136,0.25)",
              }}
            >
              <Icon.Plus /> Add Holiday
            </button>
          </div>
        </div>

        <span
          style={{
            fontSize: "13px",
            fontFamily: T.font,
            color: T.text,
            display: "block",
            marginBottom: "10px",
            letterSpacing: "0.05em",
          }}
        >
          No. of holiday{count > 1 ? "s" : ""} : <strong>{count}</strong>
        </span>

        {/* Table */}
        <div
          style={{
            background: T.white,
            borderRadius: T.radius,
            border: `1px solid ${T.border}`,
            boxShadow: T.shadow,
            overflow: "hidden",
          }}
        >
          <div className="hol-table-wrap">
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                minWidth: "520px",
              }}
            >
              <thead>
                <tr>
                  {["Holiday Name", "Date", "Actions"].map((h, i) => (
                    <th
                      key={h}
                      style={{
                        padding: "13px 20px",
                        textAlign: i === 2 ? "right" : "left",
                        fontSize: "11.5px",
                        fontFamily: T.font,
                        fontWeight: 600,
                        color: T.textFaint,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        background: T.bg,
                        borderBottom: `1px solid ${T.border}`,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {!filteredData?.length ? (
                  <tr>
                    <td
                      colSpan={3}
                      style={{
                        padding: "48px 20px",
                        textAlign: "center",
                        color: T.textFaint,
                        fontSize: "14px",
                        fontFamily: T.font,
                      }}
                    >
                      {search
                        ? `No holidays match "${search}"`
                        : "No holidays added yet. Click Add Holiday to get started."}
                    </td>
                  </tr>
                ) : (
                  filteredData.map((row) => (
                    <tr key={row.holidayId} className="hol-tr">
                      <td
                        style={{
                          padding: "14px 20px",
                          fontSize: "14px",
                          fontFamily: T.font,
                          color: T.textMid,
                          borderBottom: `1px solid ${T.borderLight}`,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <span
                            className="row-accent"
                            style={{
                              display: "inline-block",
                              width: "3px",
                              height: "20px",
                              borderRadius: "2px",
                              background: T.accent,
                              opacity: 0,
                              transition: "opacity 0.2s",
                              flexShrink: 0,
                            }}
                          />
                          <span style={{ fontWeight: 500 }}>
                            {row.holidayName}
                          </span>
                        </div>
                      </td>
                      <td
                        style={{
                          padding: "14px 20px",
                          fontSize: "14px",
                          fontFamily: T.font,
                          color: T.textMid,
                          borderBottom: `1px solid ${T.borderLight}`,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "7px",
                          }}
                        >
                          <Icon.Calendar />
                          <span
                            style={{
                              display: "inline-block",
                              padding: "3px 10px",
                              borderRadius: "20px",
                              background: T.accentBg,
                              color: T.accentText,
                              fontSize: "13px",
                              fontWeight: 500,
                              fontFamily: T.font,
                            }}
                          >
                            {formatDate(row.holidayDate)}
                          </span>
                        </div>
                      </td>
                      <td
                        style={{
                          padding: "14px 20px",
                          textAlign: "right",
                          borderBottom: `1px solid ${T.borderLight}`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        <button
                          className="hol-icon-btn"
                          title="Edit"
                          onClick={() => openEdit(row)}
                          style={{
                            width: "32px",
                            height: "32px",
                            border: `1.5px solid ${T.border}`,
                            borderRadius: T.radiusXs,
                            background: T.white,
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: T.textMuted,
                            marginLeft: "6px",
                          }}
                        >
                          <Icon.Edit />
                        </button>
                        <button
                          className="hol-icon-danger"
                          title="Delete"
                          onClick={() => requestDelete(row)}
                          style={{
                            width: "32px",
                            height: "32px",
                            border: `1.5px solid ${T.dangerBorder}`,
                            borderRadius: T.radiusXs,
                            background: T.dangerBg,
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: T.danger,
                            marginLeft: "6px",
                          }}
                        >
                          <Icon.Trash />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <HolidayModal
        open={modalOpen}
        isEdit={!!selectedHoliday}
        formData={formData}
        onChange={setFormData}
        onSave={selectedHoliday ? handleUpdateHoliday : handleAddHoliday}
        onSaveSundays={handleSaveSundays}
        onClose={() => setModalOpen(false)}
      />

      <ConfirmDialog
        config={confirmConfig}
        onConfirm={confirmConfig?.onConfirm}
        onCancel={closeConfirm}
      />
    </>
  );
}
